import { useState } from 'react';
import type { TurnoDetalle } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { paciente } from '../../api/paciente';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import type { ScreenProps } from '../../routes/types';
import { AvisoError, Boton, Cargando, FalloDeCarga, SelectorHorarios, Tarjeta, useAvisos } from '../../ui';
import { fechaLarga } from '../../ui/format';
import { Calendario } from './Calendario';

export function Reprogramar({ params }: ScreenProps) {
  const id = Number(params['id']);
  const pedido = usePedido(() => paciente.turno(id), [id]);
  if (pedido.estado === 'cargando') return <Cargando />;
  if (!pedido.datos) return <FalloDeCarga mensaje={pedido.error ?? 'No encontramos el turno'} alReintentar={pedido.recargar} />;
  return <ElegirNuevoHorario turno={pedido.datos} />;
}

function ElegirNuevoHorario({ turno: t }: { turno: TurnoDetalle }) {
  const { mostrarAviso } = useAvisos();
  const [fecha, setFecha] = useState<string | null>(null);
  const [hora, setHora] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const horarios = usePedido(
    () => (fecha ? publico.horarios(t.profesional.id_profesional, fecha, t.servicio.id_servicio) : Promise.resolve([])),
    [fecha],
  );
  // El horario actual no se ofrece: mover el turno al mismo lugar no es reprogramar.
  const libres = (horarios.datos ?? []).map((h) => ({ hora: h, ocupado: fecha === t.fecha && h === t.hora_inicio }));

  const confirmar = async () => {
    if (!fecha || !hora) return;
    setEnviando(true);
    try {
      await paciente.reprogramar(t.id_turno, fecha, hora);
      mostrarAviso({ tono: 'exito', titulo: 'Turno reprogramado', cuerpo: `Ahora es el ${fechaLarga(fecha)} a las ${hora}.` });
      irA(`/turno/${t.id_turno}`, { reemplazar: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos reprogramar el turno. Probá de nuevo.');
      setEnviando(false);
    }
  };

  return (
    <div className="reserva">
      <h1 className="reserva__titulo">Reprogramar el turno</h1>
      <p className="reserva__bajada">
        {t.servicio.nombre} con {t.profesional.nombre} {t.profesional.apellido}. Hoy es el {fechaLarga(t.fecha)} a las {t.hora_inicio}.
      </p>
      <AvisoError>{error}</AvisoError>
      <Tarjeta>
        <div className="reserva__agenda">
          <Calendario elegida={fecha} alElegir={(f) => { setFecha(f); setHora(null); }} />
          <div>
            {!fecha && <p className="reserva__nota">Elegí un día para ver los horarios libres.</p>}
            {fecha && horarios.estado === 'cargando' && <Cargando texto="Buscando horarios" />}
            {fecha && horarios.estado === 'listo' && libres.length === 0 && <p className="reserva__nota">No quedan horarios ese día.</p>}
            {libres.length > 0 && <SelectorHorarios horarios={libres} elegido={hora} alElegir={setHora} />}
          </div>
        </div>
        <div className="reserva__acciones">
          <Boton variante="secundario" onClick={() => irA(`/turno/${t.id_turno}`)}>Volver</Boton>
          <Boton disabled={!fecha || !hora} cargando={enviando} onClick={() => void confirmar()}>Confirmar nuevo horario</Boton>
        </div>
      </Tarjeta>
    </div>
  );
}
